import {
	ChangeDetectorRef,
	Component,
	Input,
	OnInit,
	OnDestroy,
	WritableSignal,
	signal,
} from '@angular/core';
import { IFileEntity } from '@app/pages/cmms/interfaces/files.interfaces';
import { IClassification } from '@app/pages/cmms/interfaces/classification.interfaces';
import { CmmsPages, ICmmsState } from '@app/pages/cmms/state/cmms.reducer';
import { TableType } from '@app/pages/cmms/types';
import { iconEnum } from '@app/configs/model/IconEnum';
import { RightSidebarNavigationService } from '@layout/au-right-sidebar/right-sidebar-navigation.service';
import { ACTIVE_PAGE_CONFIG } from '@app/pages/cmms/configs/active-page.config';
import { SetRightSidebarComponentContext } from '@layout/state/layout.actions';
import { Store } from '@ngrx/store';
import { ICMMSColumn } from '@shared/ui-components/au-table-layout/utils/interfaces';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import {
	startEditFile,
	updateActiveTab,
	updateFile,
	updateFileInsideFileEntity,
} from '@app/pages/cmms/components/files/state/files.action';
import {
	attachedEntityTableConfig,
	sidebarHeaders,
} from '@app/pages/cmms/configs/cmms-table-configs';
import { FileLoaderService } from '@shared/ui-components/au-file-loader/services/file-loader.service';
import { FileUtilities } from '@shared/ui-components/au-file-loader/helpers/file-utilities';
import { editingFile, replacingInProgress } from '../../state/files.selectors';
import { AuErrorAdapter, AuUtilsFunctions } from '@app/shared';
import { CmmsCommentsService } from '@app/pages/cmms/services/cmms-comments.service';
import { IconSnackBarService } from '@core/services/icon-snack-bar.service';
import { AuDialogService } from '@core/services/au-dialog.service';
import { IRequest } from '@app/pages/cmms/interfaces/request.interfaces';
import { FilesService } from '@app/pages/cmms/services/files.service';
import { IIncident } from '@app/pages/cmms/interfaces/incidents.interface';
import { ICMMSLocation } from '@app/pages/cmms/interfaces/cmms-global.interfaces';
import { ICmmsAsset } from '@app/pages/cmms/interfaces/assets.interfaces';
import { AuRightSidebarService } from '@core/services/au-right-sidebar.service';
import { RightSidebarAliasesEnum } from '@layout/au-right-sidebar/au-right-sidebar-component-aliases';
import { Subject, mergeMap, takeUntil } from 'rxjs';
import { CmmsSharedService } from '@app/pages/cmms/services/cmms-shared.service';
import { AuPipesModule } from '@app/shared/pipes/au-pipes.module';
import { CmmsLocationsSectionComponent } from '../../../shared-form-group-controls/cmms-locations-section/cmms-locations-section.component';
import { DocumentTypeComponent } from '../../document-type/document-type.component';
import { FileMetadataTableComponent } from './file-metadata-table/file-metadata-table.component';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CmmsLabelsSectionComponent } from '../../../shared-form-group-controls/cmms-labels-section/cmms-labels-section.component';
import { CmmsDescriptionComponent } from '../../../shared-form-group-controls/cmms-description/cmms-description.component';
import { CmmsClassificationComponent } from '../../../shared-form-group-controls/cmms-classification/cmms-classification.component';
import { AssetsSectionComponent } from '../../../assets-section/assets-section.component';
import { AuAvatarWithNameModule } from '@app/shared/ui-components';
import { EntitySectionComponent } from '../../../entity-section/entity-section.component';
import { MatIconModule } from '@angular/material/icon';
import { ActionCenterService } from '@app/core/services/action-center.service';
import { AuCommentsComponent } from '@app/shared/ui-components/au-comments/au-comments.component';
import { CommentInputComponent } from '@app/shared/ui-components/au-comments/comment-input/comment-input.component';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';
import { IWorkOrder } from '@app/domain/cmms-types/workorder-types';

@Component({
	selector: 'edit-file-entity-form',
	templateUrl: './edit-file-entity-form.component.html',
	styleUrls: ['./edit-file-entity-form.component.scss'],
	imports: [
		AuAvatarWithNameModule,
		AuPipesModule,
		CmmsLocationsSectionComponent,
		DocumentTypeComponent,
		FileMetadataTableComponent,
		CommonModule,
		MatProgressSpinnerModule,
		CmmsLabelsSectionComponent,
		CmmsDescriptionComponent,
		CmmsClassificationComponent,
		AssetsSectionComponent,
		EntitySectionComponent,
		AuCommentsComponent,
		MatIconModule,
		CommentInputComponent,
		NgxTolgeeModule,
	],
})
export class EditFileEntityFormComponent implements OnInit, OnDestroy {
	attachedWO: IWorkOrder[];
	attachedIncidents: IIncident[];
	attachedRequest: IRequest[];
	attachedAssets: ICmmsAsset[] = [];
	classifications: IClassification[];
	fileEntity: IFileEntity;
	readonly iconEnum = iconEnum;
	readonly cmmsPages = CmmsPages;
	readonly tableType = TableType;
	readonly tableConfig: ICMMSColumn[] = attachedEntityTableConfig;
	replacingInProgress = this.store$.select(replacingInProgress);
	fileForm: FormGroup = this.fb.group({});
	loadedAttachments: WritableSignal<boolean> = signal(false);

	private destroy$: Subject<void> = new Subject<void>();

	constructor(
		private sidebarNavigation: RightSidebarNavigationService,
		private store$: Store<ICmmsState>,
		private fileLoaderService: FileLoaderService,
		private fb: FormBuilder,
		private commentService: CmmsCommentsService,
		private notify: IconSnackBarService,
		private dialogService: AuDialogService,
		private filesService: FilesService,
		private cdr: ChangeDetectorRef,
		private sidebarService: AuRightSidebarService,
		private cmmsSharedService: CmmsSharedService,
		private actionCenterService: ActionCenterService,
		private translateService: TranslateService
	) {}

	@Input() set file(file: IFileEntity) {
		this.fileEntity = file;
	}

	ngOnInit(): void {
		this.store$.dispatch(
			updateActiveTab({ fileId: this.fileEntity.pk, activeTab: 'info' })
		);

		if (this.loadedWithAttachments()) {
			this.attachment(this.fileEntity);
		} else {
			this.getFileForEdit();
		}
	}

	getFileForEdit() {
		this.store$
			.select(editingFile)
			.pipe(
				takeUntil(this.destroy$),
				mergeMap(file => this.filesService.getFileEntityByPk(file.pk))
			)
			.subscribe(file => {
				this.attachment(file);
				this.file = file;
			});
	}

	loadedWithAttachments() {
		const properties = ['workOrders', 'incidents', 'requests', 'assets'];
		return properties.every(prop => this.fileEntity.hasOwnProperty(prop));
	}

	attachment(item: IFileEntity) {
		// TODO this is copy of Yaroslav`s solution.
		//  WE need refactor all this flows everywhere!!!
		this.attachedWO = [...item.workOrders];
		this.attachedIncidents = [...item.incidents];
		this.attachedRequest = [...item.requests];
		this.attachedAssets = [...item.assets];
		this.classifications = [...item.classificationsV2];
		this.loadedAttachments.set(true);
	}

	editFileEntityNotes($event: { description: string }) {
		this.store$.dispatch(
			startEditFile({
				file: {
					pk: this.fileEntity.pk,
					notes: $event.description,
					name: this.fileEntity.name,
				},
			})
		);
	}

	navigateTo(page: CmmsPages) {
		this.sidebarNavigation.setInitialComponent({
			initialComponent: RightSidebarAliasesEnum.editFile,
			config: {
				...ACTIVE_PAGE_CONFIG[CmmsPages.editFile].sidebarConfig,
				title: {
					text: 'file_id',
					endfix: this.fileEntity.fileEntityId,
				},
			},
			initialPage: CmmsPages.files,
		});

		this.store$.dispatch(
			SetRightSidebarComponentContext({
				componentContext: {
					currentEntityForEdit: this.fileEntity,
					editEntityType: 'FILE',
					actionType: 'EDIT',
				},
			})
		);

		this.cmmsSharedService.setExternalDataForNewComponent(page, this.fileEntity);
		this.sidebarNavigation.navigateTo(page, {
			...ACTIVE_PAGE_CONFIG[page].sidebarConfig,
			title: {
				text: ACTIVE_PAGE_CONFIG[page].sidebarConfig.title.toString(),
				endfix: this.fileEntity.fileEntityId,
			},
		});
	}

	navigateToExist(pageData: { page: CmmsPages; tableType: TableType }) {
		// set point where we return
		this.sidebarNavigation.setInitialComponent({
			initialComponent: RightSidebarAliasesEnum.editFile,
			config: {
				title: {
					text: 'file_id',
					endfix: this.fileEntity.fileEntityId,
				},
				showContentActions: false,
			},
			initialPage: CmmsPages.editFile,
		});
		// set context for next page
		this.store$.dispatch(
			SetRightSidebarComponentContext({
				componentContext: {
					tableTypeForExistingEntity: pageData.tableType,
					actionType: 'EDIT',
					currentEntityForEdit: this.fileEntity,
					editEntityType: 'FILE',
				},
			})
		);
		// action itself
		this.sidebarNavigation.navigateTo(
			CmmsPages.existingEntities,
			sidebarHeaders[pageData.tableType]
		);
	}

	viewSelected(item: { data: any; entityType: CmmsPages }) {
		this.sidebarService.viewCurrentEntity(
			ACTIVE_PAGE_CONFIG[CmmsPages.editFile].createComponent,
			CmmsPages.editFile,
			item,
			this.fileEntity
		);
	}

	openAssets() {
		this.sidebarNavigation.setInitialComponent({
			initialComponent: RightSidebarAliasesEnum.editFile,
			config: ACTIVE_PAGE_CONFIG[CmmsPages.editFile].sidebarConfig,
			initialPage: CmmsPages.editFile,
		});

		this.store$.dispatch(
			SetRightSidebarComponentContext({
				componentContext: {
					tableTypeForExistingEntity: TableType.ASSETS,
					currentEntityForEdit: this.fileEntity,
					editEntityType: 'FILE',
					actionType: 'EDIT',
				},
			})
		);

		this.sidebarNavigation.navigateTo(
			CmmsPages.existingEntities,
			sidebarHeaders[TableType.ASSETS]
		);
	}

	unlinkAsset(asset: ICmmsAsset) {
		const filteredAssets = this.attachedAssets?.length
			? this.attachedAssets.filter(attachedAsset => attachedAsset.pk !== asset.pk)
			: [];

		this.filesService
			.editFile({
				pk: this.fileEntity.pk,
				name: this.fileEntity.name,
				notes: this.fileEntity.notes,
				assets: filteredAssets.map(attachedAsset => {
					return { id: attachedAsset.pk, name: attachedAsset.name };
				}),
			})
			.subscribe({
				next: file => {
					this.attachedAssets = file.assets;
					this.notify.success(this.translateService.instant('success_asset_unlink'));
					this.store$.dispatch(
						updateFile({
							update: {
								id: file.pk,
								changes: file,
							},
						})
					);
					this.actionCenterService.actionDone$.next();
					this.cdr.markForCheck();
				},
			});
	}

	downloadFile() {
		const { url, name } = this.fileEntity.file;
		this.fileLoaderService.saveFileOnUserComputer(url, name);
	}

	replaceFileButtonClick(fileInput: HTMLElement) {
		fileInput.click();
	}

	replaceFile(event: Event) {
		const file = FileUtilities.getFilesFromEvent(event)[0];
		this.store$.dispatch(
			updateFileInsideFileEntity({
				newFile: file,
				fileEntity: this.fileEntity,
			})
		);
	}

	addChildControl(name: string, control: FormControl) {
		this.fileForm.addControl(name, control);
	}

	submitComment(comment: {
		commentText: string;
		sendAsEmail: boolean;
		mentions: string[];
	}) {
		this.commentService
			.addCommentToEntity('file', this.fileEntity.pk, comment)
			.subscribe({
				next: res => {
					if (!res.success) {
						this.notify.error(this.translateService.instant('error_comment_save'));
					}

					const fileEntityCopy = AuUtilsFunctions.deepCopyObject(this.fileEntity);
					fileEntityCopy.comments.unshift(res.comment);

					this.fileEntity = fileEntityCopy;
					this.cdr.detectChanges();
				},
				error: error => {
					this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
				},
			});
	}

	deleteCommentConfirmation($event: { commentId: string }) {
		const dialog = this.dialogService.openAlertDialog({
			title: this.translateService.instant('dialog_comment_delete_title'),
			description: this.translateService.instant('dialog_comment_delete_description'),
			actionBtnText: this.translateService.instant('delete'),
		});

		this.dialogService.closedByEnter(dialog).subscribe(approved => {
			if (approved) {
				this.deleteComment($event.commentId);
			}
		});
	}

	deleteComment(commentId: string) {
		this.commentService.deleteComment(commentId).subscribe({
			next: res => {
				if (!res.success) {
					this.notify.error(this.translateService.instant('error_comment_delete'));
				}

				const fileEntityCopy = AuUtilsFunctions.deepCopyObject(this.fileEntity);
				fileEntityCopy.comments = this.fileEntity.comments.filter(
					comment => comment.id !== commentId
				);

				this.fileEntity = fileEntityCopy;
				this.cdr.markForCheck();
			},
			error: error => {
				this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
			},
		});
	}

	editComment($event: { commentId: string; commentText: string }) {
		this.commentService.editEntityComment(
			$event.commentId,
			$event.commentText,
			this.fileEntity,
			(updatedEntityItem: IFileEntity) => {
				this.fileEntity = updatedEntityItem;
				this.cdr.markForCheck();
			}
		);
	}

	unlinkRequest(request: IRequest) {
		this.unlinkItem('requests', 'attachedRequest', request.pk);
	}

	unlinkWorkOrder(workOrder: IWorkOrder) {
		this.unlinkItem('workOrders', 'attachedWO', workOrder.pk);
	}

	unlinkIncident(incident: IIncident) {
		this.unlinkItem('incidents', 'attachedIncidents', incident.pk);
	}

	unlinkClassification(classification: IClassification) {
		this.unlinkItem('classifications', 'classifications', classification.pk);
	}

	unlinkItem(
		itemType: 'workOrders' | 'requests' | 'incidents' | 'classifications',
		itemArrayName,
		primaryKey: string
	): void {
		this.filesService
			.unlinkAttachedElements({
				pk: this.fileEntity.pk,
				[itemType]: primaryKey,
			})
			.subscribe({
				next: () => {
					this[itemArrayName] = this[itemArrayName].filter(
						item => item.pk !== primaryKey
					);
					this.cdr.markForCheck();
					this.store$.dispatch(
						startEditFile({
							file: {
								pk: this.fileEntity.pk,
								name: this.fileEntity.name,
								notes: this.fileEntity.notes,
							},
						})
					);
				},
				error: () => {
					this.notify.error(this.translateService.instant('error_file_update'));
				},
			});
	}

	classificationsListChanged($event: IClassification[]) {
		this.store$.dispatch(
			startEditFile({
				file: {
					pk: this.fileEntity.pk,
					name: this.fileEntity.name,
					notes: this.fileEntity.notes,
					classifications: $event.map(i => i.pk),
				},
			})
		);
	}

	locationsChanged(locations: Partial<ICMMSLocation>[]) {
		this.store$.dispatch(
			startEditFile({
				file: {
					pk: this.fileEntity.pk,
					name: this.fileEntity.name,
					notes: this.fileEntity.notes,
					locations,
				},
			})
		);
	}

	documentTypeChanged(type: string) {
		this.store$.dispatch(
			startEditFile({
				file: {
					pk: this.fileEntity.pk,
					type,
				},
			})
		);
	}

	labelsChanged($event: { labels: string[] }) {
		this.store$.dispatch(
			startEditFile({
				file: {
					pk: this.fileEntity.pk,
					labels: $event.labels,
				},
			})
		);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
